import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { EVENT_TOKEN } from 'appenv';
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import { selectLocale } from './localization';

interface Localizable<T> {
  locale: string;
  value: T;
}

export interface ProductImage {
  originalImageUrl: string;
  previewImageUrl: string;
  alt: string;
}

interface AnswerField {
  title: string;
  content: string;
}
type Tags = string[];

type LocalizableField<T> = Record<string, Localizable<T>>;
type LocalizableString = LocalizableField<string>;
type LocalizableImage = LocalizableField<ProductImage>;
type LocalizableTags = LocalizableField<Tags>;
type LocalizableAnswer = LocalizableField<Record<string, AnswerField>>;

export interface Product {
  id: string;
  name: LocalizableString;
  oneLiner: LocalizableString;
  image: LocalizableImage;
  images: LocalizableImage[];
  tags: LocalizableTags;
  boothId: Record<string, string>;
  details: LocalizableAnswer;
  eventBoothId: string;
}

export interface LocalizedProduct {
  id: string;
  name: string;
  oneLiner: string;
  image: ProductImage;
  images: ProductImage[];
  tags: Tags;
  boothId: string;
  details: Record<string, AnswerField>;
  eventBoothId: string;
}

const getProductsById = async (productId: string): Promise<Record<string, any>> => {
  const db = firebase.firestore();
  const productRef = db.collection('products').doc(productId);
  const productResponse = await productRef.get();
  const productData = productResponse.data();
  delete productData.createdOn;
  delete productData.lastModifiedOn;
  if (!productData) {
    throw new Error('Product does not exist!');
  }
  return {
    id: productId,
    ...productData,
  };
};

const getEventProductList = async () => {
  const db = firebase.firestore();
  const productListRef = db.collection('products');
  const productListResponse = await productListRef.get();
  const productList = productListResponse.docs.map((doc) => {
    const data = doc.data();
    delete data.createdOn;
    delete data.lastModifiedOn;
    return ({
      id: doc.id,
      eventBoothId: data.boothId[EVENT_TOKEN],
      ...data,
    });
  });
  return productList;
};

const productsAdapter = createEntityAdapter<Product>({
  sortComparer: (a, b) => a.name?.en.value.localeCompare(b.name?.en.value),
});
export const {
  selectAll: selectAllProducts,
  selectById: selectProductById,
} = productsAdapter.getSelectors((state: any) => state.products);

const memoizedLocalizable = {};

const isLocalizable = (fieldName: string, value: any, locale: string): boolean => {
  if (memoizedLocalizable[fieldName] !== undefined && memoizedLocalizable[fieldName][locale] !== undefined) {
    return memoizedLocalizable[fieldName][locale];
  }
  if (memoizedLocalizable[fieldName] === undefined) {
    memoizedLocalizable[fieldName] = {};
  }
  const flag = (typeof value === 'object') && !!((value as Localizable<any>)[locale] ?? (value as Localizable<any>));
  memoizedLocalizable[fieldName][locale] = flag;
  return flag;
};

const extractLocalizedValue = (
  localizationMap: Record<string, any>,
  fieldName: string,
  rawValue: any,
  locale: string,
) => {
  if (!rawValue) {
    localizationMap[fieldName] = undefined;
    return localizationMap;
  }
  if (Array.isArray(rawValue)) {
    localizationMap[fieldName] = rawValue.map((rawValueElement: {
      [locale: string]: {
        locale: string;
        value: any;
      };
    }, index: number) => {
      const localizedImage = rawValueElement[locale]?.value ?? rawValueElement.en?.value;
      if (index === 0 && fieldName === 'images') {
        localizationMap.image = localizedImage;
      }
      return localizedImage;
    });
    return localizationMap;
  }
  if (fieldName === 'details') {
    localizationMap[fieldName] = Object.entries(rawValue as LocalizableAnswer)
      .reduce(
        (localizedDetail, [detailFieldName, detailFieldValue]) => extractLocalizedValue(
          localizedDetail,
          detailFieldName,
          detailFieldValue,
          locale,
        ),
        {} as LocalizedProduct,
      );
    return localizationMap;
  }
  localizationMap[fieldName] = isLocalizable(fieldName, rawValue, locale) || isLocalizable(fieldName, rawValue, 'en')
    ? rawValue[locale]?.value ?? rawValue.en?.value
    : rawValue;
  return localizationMap;
};

const getLocalizedProduct = (product: Product, locale: string): LocalizedProduct => (
  product
    ? Object.entries(product).reduce(
      (localizedValue, [fieldName, fieldValue]) => extractLocalizedValue(
        localizedValue,
        fieldName,
        fieldValue,
        locale,
      ) as LocalizedProduct, {} as LocalizedProduct,
    ) : null
);

export const selectAllProductsByLocale = createSelector(
  [selectAllProducts, selectLocale],
  (products: Product[], locale: string): LocalizedProduct[] => products
    .map((product) => getLocalizedProduct(product, locale)),
);

export const selectAllProductsByEventBoothId = (eventBoothId: string) => createSelector(
  [selectAllProductsByLocale],
  (products: LocalizedProduct[]): LocalizedProduct[] => (products || [])
    .filter((product: LocalizedProduct) => (product.eventBoothId) === eventBoothId),
);

export const selectProductByIdAndLocale = createSelector(
  [selectProductById, selectLocale],
  getLocalizedProduct,
);

export const fetchProducts = createAsyncThunk(
  'products/fetchAll',
  async () => {
    try {
      const result = await getEventProductList();
      return result;
    } catch (error) {
      console.error(error);
    }
    return {};
  },
);

export const fetchProductsById = createAsyncThunk(
  'products/fetchById',
  async (productId: string) => {
    try {
      const result = await getProductsById(productId);
      result.eventBoothId = result.boothId[EVENT_TOKEN];
      return result;
    } catch (error) {
      console.error(error);
    }
    return {};
  },
);

export const productSlice = createSlice({
  name: 'products',
  initialState: productsAdapter.getInitialState({ fetching: true }),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchProducts.pending, (state: any) => {
      state.fetching = true;
    });
    builder.addCase(fetchProducts.fulfilled, (state: any, action) => {
      productsAdapter.upsertMany(state, action.payload);
      state.fetching = false;
    });
    builder.addCase(fetchProductsById.fulfilled, (state: any, action) => {
      productsAdapter.upsertOne(state, action.payload as Product);
      state.fetching = false;
    });
    builder.addCase(fetchProductsById.pending, (state: any) => {
      state.fetching = true;
    });
    builder.addCase(fetchProductsById.rejected, (state: any) => {
      state.fetching = false;
    });
    builder.addCase(fetchProducts.rejected, (state: any) => {
      state.fetching = false;
    });
  },
});

export default productSlice.reducer;
